import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tasksAPI, checkinsAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Sun, 
  Moon, 
  Sparkles, 
  CheckCircle2, 
  Clock,
  AlertTriangle,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const DailyOpsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('am');
  const [tasks, setTasks] = useState([]);
  const [top3Tasks, setTop3Tasks] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingTop3, setGeneratingTop3] = useState(false);
  
  // Check-in state
  const [completedTasks, setCompletedTasks] = useState([]);
  const [blockedTasks, setBlockedTasks] = useState([]);
  const [delegatedTasks, setDelegatedTasks] = useState([]);
  const [notes, setNotes] = useState('');
  const [submittingCheckin, setSubmittingCheckin] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, checkinsRes] = await Promise.all([
        tasksAPI.getAll({ status: 'pending' }),
        checkinsAPI.getAll()
      ]);
      
      setTasks(tasksRes.data);
      setCheckins(checkinsRes.data);
      
      const today = new Date().toDateString();
      const todayAMCheckin = checkinsRes.data.find(c => 
        c.check_type === 'AM' && 
        new Date(c.created_at).toDateString() === today
      );
      if (todayAMCheckin?.top_3_tasks) {
        setTop3Tasks(todayAMCheckin.top_3_tasks);
      }
    } catch (error) {
      console.error('Error fetching daily ops data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerateTop3 = async () => {
    try {
      setGeneratingTop3(true);
      const response = await aiAPI.dailyTop3();
      setTop3Tasks(response.data.top_3);
      toast.success('Top 3 tareas generadas');
    } catch (error) {
      toast.error('Error al generar tareas');
    } finally {
      setGeneratingTop3(false);
    }
  };

  const handleSubmitCheckin = async () => {
    try {
      setSubmittingCheckin(true);
      await checkinsAPI.create({
        check_type: activeTab.toUpperCase(),
        tasks_completed: completedTasks,
        tasks_blocked: blockedTasks,
        tasks_delegated: delegatedTasks,
        notes,
        top_3_tasks: activeTab === 'am' ? top3Tasks.map(t => t.task_id || t.title) : []
      });
      
      toast.success(`Check-in ${activeTab.toUpperCase()} completado`);
      setCompletedTasks([]);
      setBlockedTasks([]);
      setDelegatedTasks([]);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error('Error al guardar check-in');
    } finally {
      setSubmittingCheckin(false);
    }
  };

  const toggleTaskStatus = (taskId, status) => {
    const setters = {
      completed: setCompletedTasks,
      blocked: setBlockedTasks,
      delegated: setDelegatedTasks
    };
    
    setters[status](prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      }
      return [...prev, taskId];
    });

    Object.entries(setters).forEach(([key, setter]) => {
      if (key !== status) {
        setter(prev => prev.filter(id => id !== taskId));
      }
    });
  };

  const getTaskPriorityTasks = () => {
    return tasks.filter(t => t.priority === 'high').slice(0, 5);
  };

  const today = new Date();
  const greeting = today.getHours() < 12 ? 'Buenos días' : 'Buenas tardes';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-ops-page">
      {/* Header - Hick's Law: Clear single focus */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Daily Ops</h1>
          <p className="text-sm text-gray-500 mt-1">
            {greeting}, {user?.name?.split(' ')[0]}. {today.toLocaleDateString('es-ES', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </p>
        </div>
        <button 
          className="icon-button self-start"
          onClick={fetchData}
          aria-label="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs - Fitts's Law: Large touch targets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-gray-100 p-1.5 h-auto">
          <TabsTrigger 
            value="am" 
            className="flex-1 py-3 px-4 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-sm font-medium"
            data-testid="am-tab"
          >
            <Sun className="w-4 h-4 mr-2 text-amber-500" />
            <span className="hidden sm:inline">Inicio del Día</span>
            <span className="sm:hidden">AM</span>
          </TabsTrigger>
          <TabsTrigger 
            value="pm"
            className="flex-1 py-3 px-4 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-sm font-medium"
            data-testid="pm-tab"
          >
            <Moon className="w-4 h-4 mr-2 text-indigo-500" />
            <span className="hidden sm:inline">Cierre del Día</span>
            <span className="sm:hidden">PM</span>
          </TabsTrigger>
        </TabsList>

        {/* AM Tab - Miller's Law: Limited to 3 tasks */}
        <TabsContent value="am" className="space-y-6">
          {/* Top 3 Tasks - Miller's Law: Only 3 items */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <h2 className="text-base font-semibold text-gray-900">Top 3 Tareas de Hoy</h2>
                <span className="text-xs text-gray-400">(Miller's Law: Enfócate solo en 3)</span>
              </div>
              {/* Fitts's Law: Prominent action button */}
              <Button
                onClick={handleGenerateTop3}
                disabled={generatingTop3}
                className="action-button-primary bg-amber-500 hover:bg-amber-600"
                data-testid="generate-top3-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generatingTop3 ? 'Generando...' : 'Generar con IA'}
              </Button>
            </div>

            {top3Tasks.length > 0 ? (
              <div className="space-y-3">
                {top3Tasks.map((task, idx) => (
                  <div 
                    key={idx}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{task.title}</p>
                        <p className="text-sm text-gray-500 mt-1">{task.reason || task.kr_title}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 bg-gray-200 rounded text-gray-600">
                            Esfuerzo: {task.effort}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No hay tareas definidas para hoy.</p>
                <p className="text-sm text-gray-400 mt-1">Genera tu Top 3 con IA</p>
              </div>
            )}
          </div>

          {/* High Priority Tasks */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Tareas de Alta Prioridad
            </h2>
            
            {getTaskPriorityTasks().length > 0 ? (
              <div className="space-y-2">
                {getTaskPriorityTasks().map((task) => (
                  <div 
                    key={task.task_id}
                    className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100"
                  >
                    <Clock className="w-4 h-4 text-red-400" />
                    <span className="flex-1 text-sm text-gray-700">{task.title}</span>
                    <span className="text-xs text-gray-500 font-medium">{task.effort}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                No hay tareas de alta prioridad pendientes
              </p>
            )}
          </div>

          {/* AM Check-in */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Check-in Matutino</h2>
            <div className="space-y-4">
              <Textarea
                placeholder="Notas del inicio del día, intenciones, bloqueos anticipados..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-gray-50 border-gray-200 min-h-[100px] focus:bg-white focus:border-gray-300"
                data-testid="am-notes-input"
              />
              <Button 
                onClick={handleSubmitCheckin}
                disabled={submittingCheckin}
                className="bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="submit-am-checkin"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {submittingCheckin ? 'Guardando...' : 'Completar Check-in AM'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* PM Tab */}
        <TabsContent value="pm" className="space-y-6">
          {/* Task Review */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Revisión de Tareas</h2>
            <p className="text-sm text-gray-500 mb-4">
              Marca el estado de tus tareas del día
            </p>

            {tasks.length > 0 ? (
              <div className="space-y-2">
                {tasks.slice(0, 10).map((task) => (
                  <div 
                    key={task.task_id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <span className="flex-1 text-sm text-gray-700">{task.title}</span>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={completedTasks.includes(task.task_id) ? 'default' : 'outline'}
                        className={completedTasks.includes(task.task_id) 
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white border-0'
                          : 'border-gray-200 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200'
                        }
                        onClick={() => toggleTaskStatus(task.task_id, 'completed')}
                        data-testid={`complete-task-${task.task_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant={blockedTasks.includes(task.task_id) ? 'default' : 'outline'}
                        className={blockedTasks.includes(task.task_id) 
                          ? 'bg-red-500 hover:bg-red-600 text-white border-0'
                          : 'border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                        }
                        onClick={() => toggleTaskStatus(task.task_id, 'blocked')}
                        data-testid={`block-task-${task.task_id}`}
                      >
                        <AlertTriangle className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant={delegatedTasks.includes(task.task_id) ? 'default' : 'outline'}
                        className={delegatedTasks.includes(task.task_id) 
                          ? 'bg-blue-500 hover:bg-blue-600 text-white border-0'
                          : 'border-gray-200 text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200'
                        }
                        onClick={() => toggleTaskStatus(task.task_id, 'delegated')}
                        data-testid={`delegate-task-${task.task_id}`}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                No hay tareas pendientes
              </p>
            )}
          </div>

          {/* PM Check-in */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Check-in Vespertino</h2>
            
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                <p className="text-2xl font-semibold text-emerald-600">{completedTasks.length}</p>
                <p className="text-xs text-emerald-700">Completadas</p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                <p className="text-2xl font-semibold text-red-600">{blockedTasks.length}</p>
                <p className="text-xs text-red-700">Bloqueadas</p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <p className="text-2xl font-semibold text-blue-600">{delegatedTasks.length}</p>
                <p className="text-xs text-blue-700">Delegadas</p>
              </div>
            </div>

            <div className="space-y-4">
              <Textarea
                placeholder="Notas del cierre del día, aprendizajes, pendientes para mañana..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-gray-50 border-gray-200 min-h-[100px] focus:bg-white focus:border-gray-300"
                data-testid="pm-notes-input"
              />
              <Button 
                onClick={handleSubmitCheckin}
                disabled={submittingCheckin}
                className="bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="submit-pm-checkin"
              >
                <Moon className="w-4 h-4 mr-2" />
                {submittingCheckin ? 'Guardando...' : 'Completar Check-in PM'}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Recent Check-ins */}
      {checkins.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Check-ins Recientes</h2>
          <div className="space-y-2">
            {checkins.slice(0, 5).map((checkin) => (
              <div 
                key={checkin.checkin_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {checkin.check_type === 'AM' ? (
                    <Sun className="w-4 h-4 text-amber-500" />
                  ) : (
                    <Moon className="w-4 h-4 text-indigo-500" />
                  )}
                  <span className="text-sm font-medium text-gray-700">{checkin.check_type}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-emerald-600 font-medium">
                    {checkin.tasks_completed?.length || 0} completadas
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(checkin.created_at).toLocaleDateString('es-ES')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyOpsPage;
